import { MotionLoader } from "./Brand.js";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ApiError } from "./api.js";
import { t } from "./i18n.js";
import { Icon, type IconName } from "./Icon.js";
import { Link } from "../app/router.js";
import { isTopLayer, useLatest, useLayer } from "./hooks.js";
import { humanError } from "./errors.js";

export function Banner({ kind = "info", icon, children, action }: { kind?: "info" | "warn" | "bad"; icon?: IconName; children: ReactNode; action?: ReactNode }) {
  const name: IconName = icon ?? (kind === "info" ? "info" : "alert");
  return (
    <div className={`banner ${kind}`} role={kind === "bad" ? "alert" : "status"}>
      <Icon name={name} size="sm" />
      <div className="grow">{children}</div>
      {action}
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const offline = error instanceof ApiError && error.status === 0;
  return (
    <Banner
      kind={offline ? "warn" : "bad"}
      icon={offline ? "wifi" : "alert"}
      action={
        onRetry ? (
          <button className="btn sm quiet" onClick={onRetry}>
            {t("common.retry")}
          </button>
        ) : undefined
      }
    >
      {humanError(error)}
    </Banner>
  );
}

export function Loading({ label = t("common.loading") }: { label?: string }) {
  return (
    <div className="loading" aria-busy="true" role="status">
      <MotionLoader type="levels" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

// ——— Feuilles, menus, dialogues ————————————————————————————————————————————

/**
 * Une feuille : du bas sur téléphone (comme CapCut / Edits), au centre sur ordinateur, ou
 * « docked » dans le panneau de droite de l'écran de montage. Échap et le voile la ferment.
 */
export function Sheet({
  title,
  onClose,
  children,
  footer,
  className = "",
  testId,
  docked = false,
  size = "md",
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  testId?: string;
  docked?: boolean;
  size?: "md" | "lg" | "full";
}) {
  const close = useRef(onClose);
  close.current = onClose;
  // Des feuilles empilées (une fenêtre de confirmation sur une feuille) : Échap ne ferme que celle du dessus.
  const layer = useLayer();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && isTopLayer(layer.current) && close.current();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [layer]);
  const sheet = (
    <div className={`sheet-layer${docked ? " docked" : ""}`}>
      <div className="scrim" onClick={() => close.current()} />
      <section className={`sheet ${size} ${className}`} role="dialog" aria-modal={!docked} aria-label={typeof title === "string" ? title : undefined} data-testid={testId}>
        <span className="grabber" aria-hidden="true" />
        {title !== undefined && (
          <header className="sheet-head">
            <h2>{title}</h2>
            <button className="icon-btn" onClick={() => close.current()} aria-label={t("common.close")}>
              <Icon name="x" />
            </button>
          </header>
        )}
        <div className="sheet-body">{children}</div>
        {footer && <footer className="sheet-foot">{footer}</footer>}
      </section>
    </div>
  );
  // Posée sur <body> : ouverte depuis la barre latérale (position sticky, son propre empilement),
  // elle passait sous les vignettes de la page. Docked, elle reste dans le panneau de l'éditeur.
  return docked ? sheet : createPortal(sheet, document.body);
}

/** `checked` : un choix parmi plusieurs (l'espace courant) ; la coche s'affiche, les autres gardent l'alignement. */
export type MenuItem = { label: string; icon?: IconName; danger?: boolean; onSelect: () => void; testId?: string; checked?: boolean };

/** Un menu : liste déroulante sur ordinateur, feuille d'actions sur téléphone. */
export function Menu({
  label,
  items,
  trigger,
  triggerClass = "icon-btn",
  className = "",
  header,
  open: controlled,
  onOpenChange,
}: {
  label: string;
  items: MenuItem[];
  trigger?: ReactNode;
  triggerClass?: string;
  className?: string;
  header?: string;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
}) {
  const [own, setOwn] = useState(false);
  const open = controlled ?? own;
  const setOpen = (o: boolean) => (onOpenChange ? onOpenChange(o) : setOwn(o));
  const box = useRef<HTMLDivElement>(null);
  // Posés à l'ouverture seulement (pas à chaque rendu) ; la fermeture lit la fonction du moment.
  const close = useLatest(() => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => box.current && !box.current.contains(e.target as Node) && close.current();
    const esc = (e: KeyboardEvent) => e.key === "Escape" && close.current();
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open, close]);
  return (
    <div className={`menu-wrap ${className}`} ref={box}>
      <button className={triggerClass} aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen(!open); }} data-testid="menu">
        {trigger ?? <Icon name="more" />}
      </button>
      {open && (
        <>
          <div className="menu-scrim" onClick={() => setOpen(false)} />
          <div className="menu" role="menu">
            {header && <div className="menu-head">{header}</div>}
            {items.map((it) => (
              <button
                key={it.label}
                role={it.checked === undefined ? "menuitem" : "menuitemradio"}
                aria-checked={it.checked}
                className={`menu-item${it.danger ? " danger" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  it.onSelect();
                }}
                data-testid={it.testId}
              >
                {it.checked !== undefined ? <span className="menu-check">{it.checked && <Icon name="check" size="sm" />}</span> : it.icon && <Icon name={it.icon} size="sm" />}
                <span className="ellipsis">{it.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Une petite fenêtre (renommer, confirmer) : une feuille sur téléphone. */
export function Dialog({ title, children, onClose, actions }: { title: string; children?: ReactNode; onClose: () => void; actions: ReactNode }) {
  return (
    <Sheet title={title} onClose={onClose} className="dialog" footer={<div className="row end">{actions}</div>}>
      {children}
    </Sheet>
  );
}

/** Deux à quatre choix exclusifs (Rushes · Montage · Export ; Templates · Sous-titres · Musique). */
export function Segmented<T extends string>({ value, options, onChange, label, className = "" }: { value: T; options: { id: T; label: string; testId?: string }[]; onChange: (v: T) => void; label: string; className?: string }) {
  return (
    <div className={`segmented ${className}`} role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} role="tab" aria-selected={value === o.id} className={value === o.id ? "on" : ""} onClick={() => onChange(o.id)} data-testid={o.testId}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Le cœur des favoris, partout le même. */
export function Heart({ on, onToggle, label, className = "" }: { on: boolean; onToggle: () => void; label: string; className?: string }) {
  return (
    <button className={`heart${on ? " on" : ""} ${className}`} aria-pressed={on} aria-label={label} onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid="heart">
      <Icon name="heart" fill={on} />
    </button>
  );
}

/** Un titre d'écran court, sans sous-titre descriptif (doc 12 §12.6). */
export function PageHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <header className="page-head">
      <h1>{title}</h1>
      {children}
    </header>
  );
}

export function BackLink({ to, label = t("common.back"), icon = "left" }: { to: string; label?: string; icon?: IconName }) {
  return (
    <Link className="icon-btn" to={to} aria-label={label}>
      <Icon name={icon} />
    </Link>
  );
}
